/**
 * @desc 业务请求方法
 * @time 2023-07-31 15:36:56
 * @user simxin
 */

import {
  getUin,
  getSaasToken,
  getOwnUin,
  getSid,
  gotoLogin,
  saveLogin,
  getCookie,
} from '@src/utils/cookies';
import { usePortalStore } from '@src/store';
import { changeAccountEvent } from '@src/component/alarm/account';
import { getQueryString, clearQueryString } from '@src/utils/tools/url';
import { adminAPIRequest, trtcCGIRequest, proxyAPIRequest } from './request';
import { createLoginReport } from '../report';
import { setAegisUin } from '@src/monitor/aegis';
import { reportLogin } from '../report/aegis';
import { getMenu } from '@src/mock/console';

/**
 * @desc 缓存 console 信息
 */
let consoleCache: Application.Console[];

/**
 * @desc 初始化新控制台全局标识
 */
const initNewConsole = () => {
  document.body.classList.add('is-v2');
};

/**
 * @desc 获取所有应用信息
 */
export const requestConsole = async () => {
  initNewConsole();
  if (!consoleCache) {
    const ret = await adminAPIRequest<'console/queryConsole'>(
      'console/queryConsole',
      {},
    );
    // const ret: any = { data: [] };
    // ret.data = getMenu(window.VERSION);
    // 只保留 product 为 deskbuddy 的数据
    const filteredData = ret.data?.filter((item:any) => item.product === 'deskbuddy') || [];

    // 全量：一阶段用getMenu，二阶段用配置系统返回值
    consoleCache = filteredData;
  }
  const { setLoadedPortal } = usePortalStore.getState();
  setLoadedPortal(true);
  return consoleCache;
};

/**
 * @desc 页面加载前的前置接口
 */
export const rqeuestPredependeAPI = async (): Promise<boolean> => {
  /**
   * @desc
   * 1. 优先判断 query 用户态
   * 2. 同步官网登录态
   * 3. 获取 cookie 登录态
   * 4. 通过获取昵称校验 token 有效性
   */
  const saas: any = {};
  const query_uin = getQueryString('uin');
  const query_ownerUin = getQueryString('ownerUin');
  const query_token = getQueryString('key');
  const query_isOldDomain = getQueryString('isOldDomain');
  if (query_uin && query_ownerUin && query_token) {
    clearQueryString();
    Object.assign(saas, {
      saas_uin: query_uin,
      saas_ownerUin: query_ownerUin,
      saas_token: query_token,
      saas_isOldDomain: query_isOldDomain,
    });
    saveLogin(saas);
    setAegisUin();
    createLoginReport('loginHasQueryParam');
    reportLogin('fromCloud', saas);
  } else {
    const GET_FROM_SAAS_URL_OLD = `https://intl.cloud.tencent.com/account/login/saas/getTokenFormSass`;
    const GET_FROM_SAAS_URL_NEW = `https://www.tencentcloud.com/account/login/saas/getTokenFormSass`;
    const cookieIsOldDomain = getCookie('saas_isOldDomain') === '1';
    createLoginReport('loginGetTokenFormSassStart');
    // 上报的 通常会一个成功 一个失败，，，但是这种情况是正常的
    const requests = [
      requestGetTokenFromSaas(GET_FROM_SAAS_URL_NEW).catch(() => null),
      requestGetTokenFromSaas(GET_FROM_SAAS_URL_OLD).catch(() => null),
    ];

    const [respNew, respOld] = await Promise.all(requests);
    createLoginReport('loginGetTokenFormSassEnd');
    if (respNew || respOld) {
      createLoginReport('loginGetTokenFormSassSuccess');
      const resp = (
        cookieIsOldDomain ? respOld || respNew : respNew || respOld
      )!;
      const isOldflag = !!(
        (cookieIsOldDomain && respOld) ||
        (!respNew && respOld)
      );
      Object.assign(saas, {
        saas_uin: resp.saas_uin,
        saas_ownerUin: resp.saas_ownerUin,
        saas_token: resp.saas_token,
        saas_isOldDomain: isOldflag ? '1' : '0',
      });
      saveLogin(saas);
      reportLogin('getTokenFormSass', saas);
      setAegisUin();
    } else {
      const cookie_uin = getUin();
      const cookie_ownerUin = getOwnUin();
      const cookie_token = getSaasToken();
      const cookie_sid = getSid();
      let valid = false;
      createLoginReport('loginGetTokenFormSassFailed');
      if (cookie_uin) {
        setAegisUin();
      }
      if (cookie_uin && cookie_ownerUin && cookie_token) {
        createLoginReport('loginGetTokenFormSassFailedHasCookie');
        Object.assign(saas, {
          saas_uin: cookie_uin,
          saas_ownerUin: cookie_ownerUin,
          saas_token: cookie_token,
        });

        if (cookie_sid) {
          createLoginReport('loginCommonMcStart');
          valid = await requestUserStatus();
          if (!valid) {
            createLoginReport('loginCommonMcFailedGotoLogin');
            gotoLogin();
            // hack 一下，避免执行后面的逻辑
            return await new Promise(resolve => {
              setTimeout(() => {
                resolve(false);
              }, 10000);
            });
          }
          createLoginReport('loginCommonMcsuccess');
        }
      } else {
        createLoginReport('loginGetTokenFormSassFailedNoCookieGotoLogin');
        gotoLogin();
        // hack 一下，避免执行后面的逻辑
        return await new Promise(resolve => {
          setTimeout(() => {
            resolve(false);
          }, 10000);
        });
      }
    }
  }

  const hostMap = {
    old: 'intl.cloud.tencent.com',
    new: 'tencentcloud.com',
  };
  const isOldDomain = getCookie('saas_isOldDomain') === '1';
  const subDomain = isOldDomain ? hostMap.old : hostMap.new;
  window.QCBUY_HOST = `buy.${subDomain}`; // done buy.intl.cloud.tencent.com/
  window.QCMAIN_HOST = isOldDomain ? hostMap.old : `www.${subDomain}`; // done intl.cloud.tencent.com，eg:/document/product/651/13334
  window.QCLOUD_ROOT_HOST = `${subDomain}`; // nouse  intl.cloud.tencent.com
  window.QCCONSOLE_HOST = `console.${subDomain}`; // done console.intl.cloud.tencent.com
  window.AVAPI_CLOUD_HOST = `https://${window.QCCONSOLE_HOST}`; // nouse

  createLoginReport('loginRequestCheckLoginStart');
  // 校验登录态
  setAegisUin();
  const active = await requestCheckLogin();
  if (!active) {
    createLoginReport('loginRequestCheckLoginFailedGotoLogin');
    gotoLogin();
    // hack 一下，避免执行后面的逻辑
    return await new Promise(resolve => {
      setTimeout(() => {
        resolve(false);
      }, 10000);
    });
  }
  createLoginReport('loginRequestCheckLoginSuccess');

  const ret: 'pass' | 'fail' | 'err' = await Promise.all([
    // requestUserStatus(),
    // requestGetMail(),
    requestGetAccountInfo(),
    requestGetName(),
  ]).then<'pass' | 'fail' | 'err'>(res => {
    let passed: 'pass' | 'fail' | 'err' = 'pass';
    res.forEach(e => {
      if (typeof e === 'string') {
        const { setErrorCode } = usePortalStore.getState();
        switch (e) {
          case 'InternalError.GetAppIdError':
            setErrorCode('InternalError.GetAppIdError');
        }
        passed = 'err';
      }
      if (passed === 'pass') {
        passed = Boolean(e) ? 'pass' : 'fail';
      }
    });
    return passed;
  });

  /**
   * @desc 失败需要登录
   */
  if (ret === 'fail') {
    createLoginReport('loginGetAccountInfoFailedGotoLogin');
    gotoLogin();
    // hack 一下，避免执行后面的逻辑
    return await new Promise(resolve => {
      setTimeout(() => {
        resolve(false);
      }, 10000);
    });
  }
  createLoginReport('loginSuccess');

  /**
   * @desc 错误需要提示
   */
  if (ret === 'err') {
    return false;
  }
  window.AegisV2?.setConfig?.({
    uid: getUin(),
  });
  /**
   * @desc 判断登录态后监听账号变更
   */
  changeAccountEvent(saas?.saas_uin, saas?.saas_token);
  return true;
};

/**
 * @desc 获取用户昵称
 */
export const requestGetName = async (): Promise<boolean | string> => {
  return new Promise(resolve => {
    proxyAPIRequest({
      regionId: 9,
      serviceType: 'account',
      cmd: 'DescribeNickname',
      data: { Version: '2018-12-25' },
    }).then(ret => {
      if (!ret || ret.code) {
        if (ret.code === 'InternalError.GetAppIdError') {
          resolve('InternalError.GetAppIdError');
        } else {
          // 获取不到昵称，不用跳
          // resolve(false);
          resolve(true);
        }
      } else {
        const { setNickname } = usePortalStore.getState();
        setNickname(ret.data.Response.Nickname);
        resolve(true);
      }
    });
  });
};

/**
 * @desc 获取用户信息
 */
export const requestGetAccountInfo = async (): Promise<boolean | string> => {
  return new Promise(resolve => {
    proxyAPIRequest({
      regionId: 9,
      serviceType: 'account',
      cmd: 'DescribeAccountInfo',
      data: { Version: '2018-12-25' },
    }).then(ret => {
      if (!ret || ret.code) {
        if (ret.code === 'InternalError.GetAppIdError') {
          resolve('InternalError.GetAppIdError');
        } else {
          resolve(false);
        }
      } else {
        const { AppId, LoginUin, Nickname, OwnerUin } = ret.data.Response;
        window.LOGIN_INFO.appId = AppId;
        window.LOGIN_INFO.loginUin = LoginUin;
        window.LOGIN_INFO.ownerUin = OwnerUin;
        window.LOGIN_INFO.isOwner = LoginUin === OwnerUin;
        window.LOGIN_INFO.nickname = Nickname;
        resolve(true);
      }
    });
  });
};

/**
 * @desc 获取用户邮箱
 */
export const requestGetMail = async (): Promise<boolean> => {
  return new Promise(resolve => {
    proxyAPIRequest({
      regionId: 9,
      serviceType: 'account',
      cmd: 'DescribeBindInfo',
      data: { Version: '2018-12-25' },
    }).then(ret => {
      if (!ret || ret.code) {
        resolve(false);
      } else {
        const { setMail } = usePortalStore.getState();
        const list = ret.data.Response.BindList.filter(
          (e: { Type: string }) => e.Type === 'mail',
        );
        setMail(list?.[0]?.Account);
        resolve(true);
      }
    });
  });
};

/**
 * @desc 获取站点信息
 */
export const requestGetSiteProperties = async (): Promise<boolean> => {
  return new Promise(resolve => {
    trtcCGIRequest('common/base/GetSiteProperties', {}).then(site => {
      window.LOGIN_INFO.area = +site.IsIntlAccount + 1;
      resolve(true);
    });
  });
};

/**
 * @desc 获取登录态 skey
 */
export const requestUserStatus = async (): Promise<boolean> => {
  return new Promise(resolve => {
    trtcCGIRequest('common/mc', {
      Action: 'getSkey',
      Data: {
        SaaSToken: getSaasToken(),
        platform: 'intlSaaSTrtc',
        clientUA: navigator.userAgent,
        uin: getUin(),
        ownerUin: getOwnUin(),
        sid: getSid(),
      },
    }).then(ret => {
      resolve(ret && !ret.code);
    });
  });
};

/**
 * @desc 校验登录态
 */
export const requestCheckLogin = async (): Promise<boolean> => {
  return new Promise(resolve => {
    trtcCGIRequest('common/checkLogin', {}).then(ret => {
      resolve(ret && !ret.code);
    });
  });
};

/**
 * @desc 同步控制台登录态到独立站
 */
export const requestGetTokenFromSaas = async (
  baseUrl: string,
): Promise<LoginCookies> => {
  const ret = await fetch(
    `${baseUrl}?${new URLSearchParams({
      platform: 'intlSaaSTrtc',
      s_url: /dev|pre|test/.test(window.location.origin)
        ? 'test.trtc.io'
        : 'trtc.io', // 固定值
      clientUA: navigator.userAgent,
    })}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );
  const { code, data } = await ret.json();
  if (code === 0) {
    return {
      saas_token: data.key,
      saas_uin: data.uin,
      saas_ownerUin: data.ownerUin,
    };
  }
  return null!;
};
